import { globalVFS } from './storage/VFS';
import {
  bodyOBJ,
  hipsOBJ,
  leftArmOBJ,
  rightArmOBJ,
  leftUpperLegOBJ,
  rightUpperLegOBJ,
  leftLowerLegOBJ,
  rightLowerLegOBJ,
  leftFootOBJ,
  rightFootOBJ,
  // Light Mechs
  locustBodyOBJ,
  locustLeftArmOBJ,
  locustRightArmOBJ,
  locustLeftLegOBJ,
  locustRightLegOBJ,
  jennerBodyOBJ,
  jennerLeftArmOBJ,
  jennerRightArmOBJ,
  jennerLeftLegOBJ,
  jennerRightLegOBJ,
  ravenBodyOBJ,
  ravenLeftArmOBJ,
  ravenRightArmOBJ,
  ravenLeftLegOBJ,
  ravenRightLegOBJ
} from './EmbeddedOBJData';

export class GameInstaller {
  /**
   * Install MekWarrior game files into the VFS
   * Creates proper game directory structure like a real game installation
   */
  async installMekWarrior(): Promise<void> {
    console.log('Installing MekWarrior to C:\\Games\\MekWarrior...');
    
    try {
      // Create base game directories
      await globalVFS.createDirectory('C:\\Games');
      await globalVFS.createDirectory('C:\\Games\\MekWarrior');
      await globalVFS.createDirectory('C:\\Games\\MekWarrior\\Mechs');
      await globalVFS.createDirectory('C:\\Games\\MekWarrior\\Mechs\\Annihilator');
      await globalVFS.createDirectory('C:\\Games\\MekWarrior\\Mechs\\Locust');
      await globalVFS.createDirectory('C:\\Games\\MekWarrior\\Mechs\\Jenner');
      await globalVFS.createDirectory('C:\\Games\\MekWarrior\\Mechs\\Raven');
      await globalVFS.createDirectory('C:\\Games\\MekWarrior\\Data');
      await globalVFS.createDirectory('C:\\Games\\MekWarrior\\Config');
      await globalVFS.createDirectory('C:\\Games\\MekWarrior\\Save');
      await globalVFS.createDirectory('C:\\Games\\MekWarrior\\Missions');
      
      // Install game configuration
      await this.createGameConfig();
      
      // Install all mech files
      await this.installAnnihilatorMech();
      await this.installLightMechs();
      
      // Notify FileExplorer that files have been added
      window.dispatchEvent(new CustomEvent('fileSystemChanged', {
        detail: { path: 'C:\\Games\\MekWarrior\\Mechs', type: 'gameInstalled' }
      }));
      
      console.log('MekWarrior installation complete!');
    } catch (error) {
      console.error('Failed to install MekWarrior:', error);
      throw error;
    }
  }

  private async createGameConfig(): Promise<void> {
    // Main game configuration
    const gameConfig = `// MekWarrior Game Configuration
Version=2.1
GraphicsAPI=OpenGL
Resolution=1024x768
SoundEnabled=true
MusicEnabled=true
MouseSensitivity=0.5
KeyboardLayout=QWERTY

// Graphics Settings
Shadows=true
Lighting=true
TextureDetail=High
ParticleEffects=true
ViewDistance=1000

// Gameplay Settings
Difficulty=Veteran
AutosaveEnabled=true
AutosaveInterval=300
CockpitShake=true
WeaponEffects=true`;

    await globalVFS.save('C:\\Games\\MekWarrior\\Config\\game.cfg', gameConfig);

    // Controls configuration
    const controlsConfig = `// MekWarrior Control Configuration
[Movement]
Forward=W
Backward=S
TurnLeft=A
TurnRight=D
Jump=Space
Shutdown=Ctrl+S

[Weapons]
FireGroup1=LeftMouse
FireGroup2=RightMouse
FireGroup3=R
FireGroup4=T
CycleTargets=Tab
LockTarget=L

[View]
ZoomIn=Z
ZoomOut=X
CockpitView=F1
ExternalView=F2
TacticalView=F3`;

    await globalVFS.save('C:\\Games\\MekWarrior\\Config\\controls.cfg', controlsConfig);

    // Readme file
    const readme = `MekWarrior - Tactical BattleMech Combat Simulator
===============================================

Welcome to the world of BattleTech!

SYSTEM REQUIREMENTS:
- Pentium 75 MHz processor or better
- 16 MB RAM
- SVGA graphics card
- Sound Blaster compatible sound card
- 50 MB hard disk space

INSTALLATION:
The game has been installed to C:\\Games\\MekWarrior\\

GETTING STARTED:
1. Launch the game from the desktop icon
2. Complete the training missions
3. Customize your mech loadout
4. Enter combat and destroy enemy mechs!

CONTROLS:
See Config\\controls.cfg for complete control layout

MECHS AVAILABLE:
- Annihilator (100 tons) - Heavy Assault Mech
  * Dual AC/20 autocannons
  * Twin LRM-15 missile systems
  * Heavy armor protection

TECHNICAL SUPPORT:
For technical issues, check the log files in the Data\\ directory.

Good hunting, MechWarrior!`;

    await globalVFS.save('C:\\Games\\MekWarrior\\README.TXT', readme);
  }

  private async installAnnihilatorMech(): Promise<void> {
    // Install OBJ files directly from embedded data
    const objFiles = {
      'body.obj': await this.getBodyObjContent(),
      'hips.obj': await this.getHipsObjContent(),
      'leftarm.obj': await this.getLeftArmObjContent(),
      'rightarm.obj': await this.getRightArmObjContent(),
      'leftupperleg.obj': await this.getLeftUpperLegObjContent(),
      'rightupperleg.obj': await this.getRightUpperLegObjContent(),
      'leftlowerleg.obj': await this.getLeftLowerLegObjContent(),
      'rightlowerleg.obj': await this.getRightLowerLegObjContent(),
      'leftfoot.obj': await this.getLeftFootObjContent(),
      'rightfoot.obj': await this.getRightFootObjContent()
    };

    for (const [fileName, content] of Object.entries(objFiles)) {
      const vfsPath = `C:\\Games\\MekWarrior\\Mechs\\Annihilator\\${fileName}`;
      await globalVFS.save(vfsPath, content);
      console.log(`Installed ${fileName} to VFS`);
    }

    // Create mech configuration file
    const annihilatorMekConfig = `// Annihilator BattleMech Configuration
[General]
Name="ANH-1A Annihilator"
Weight=100
Class=Assault
Manufacturer="Earthwerks Incorporated"
Model="ANH-1A"
Year=3048

[Performance]
MaxSpeed=32.4
JumpCapacity=0
HeatSinks=20
ArmorType=Standard
Structure=Standard

[Weapons]
Weapon1="Autocannon/20"
Weapon1Location=RightArm
Weapon1Ammo=10

Weapon2="Autocannon/20" 
Weapon2Location=LeftArm
Weapon2Ammo=10

Weapon3="LRM-15"
Weapon3Location=RightTorso
Weapon3Ammo=16

Weapon4="LRM-15"
Weapon4Location=LeftTorso
Weapon4Ammo=16

[Armor]
Head=30
CenterTorso=80
CenterTorsoRear=25
RightTorso=65
RightTorsoRear=20
LeftTorso=65
LeftTorsoRear=20
RightArm=50
LeftArm=50
RightLeg=65
LeftLeg=65

[Description]
"The Annihilator is the ultimate expression of firepower over mobility. 
This 100-ton assault mech carries enough weaponry to level a city block, 
but moves at the speed of continental drift. When an Annihilator appears 
on the battlefield, smart pilots give it a wide berth."`;

    await globalVFS.save('C:\\Games\\MekWarrior\\Mechs\\Annihilator\\Annihilator.mek', annihilatorMekConfig);

    // Create pilot briefing file
    const briefing = `MISSION BRIEFING - ANNIHILATOR DEPLOYMENT
========================================

Pilot: [CLASSIFIED]
Mech: ANH-1A Annihilator
Callsign: "Slowpoke"

SITUATION:
Enemy forces have fortified positions in sector 7-G. 
Intelligence reports heavy resistance including multiple 
lance formations and defensive emplacements.

MISSION OBJECTIVES:
Primary: Eliminate all hostile mechs
Secondary: Destroy enemy supply depot
Tertiary: Preserve ammunition for follow-up missions

TACTICAL ANALYSIS:
The Annihilator's twin AC/20s can core any mech in a single 
salvo at close range. Use the LRM systems for long-range 
softening before closing to optimal AC/20 range.

WARNING: Heat buildup is extreme with full weapons fire. 
Manage heat carefully or risk shutdown.

RECOMMENDED STRATEGY:
1. Advance slowly while using LRMs
2. Let enemies come to you - use your range advantage
3. Save AC/20 ammo for priority targets
4. Position near cover to break line of sight when overheating

Good hunting, MechWarrior!`;

    await globalVFS.save('C:\\Games\\MekWarrior\\Missions\\Annihilator_Briefing.txt', briefing);
  }

  // Methods to get OBJ file content from embedded assets
  private async getBodyObjContent(): Promise<string> {
    return bodyOBJ;
  }

  private async getHipsObjContent(): Promise<string> {
    return hipsOBJ;
  }

  private async getLeftArmObjContent(): Promise<string> {
    return leftArmOBJ;
  }

  private async getRightArmObjContent(): Promise<string> {
    return rightArmOBJ;
  }

  private async getLeftUpperLegObjContent(): Promise<string> {
    return leftUpperLegOBJ;
  }

  private async getRightUpperLegObjContent(): Promise<string> {
    return rightUpperLegOBJ;
  }

  private async getLeftLowerLegObjContent(): Promise<string> {
    return leftLowerLegOBJ;
  }

  private async getRightLowerLegObjContent(): Promise<string> {
    return rightLowerLegOBJ;
  }

  private async getLeftFootObjContent(): Promise<string> {
    return leftFootOBJ;
  }

  private async getRightFootObjContent(): Promise<string> {
    return rightFootOBJ;
  }

  private async installLightMechs(): Promise<void> {
    // Install Locust Light Mech
    console.log('Installing Locust Light Mech...');
    const locustObjFiles = {
      'body.obj': locustBodyOBJ,
      'leftarm.obj': locustLeftArmOBJ,
      'rightarm.obj': locustRightArmOBJ,
      'leftleg.obj': locustLeftLegOBJ,
      'rightleg.obj': locustRightLegOBJ
    };

    for (const [fileName, content] of Object.entries(locustObjFiles)) {
      const vfsPath = `C:\\Games\\MekWarrior\\Mechs\\Locust\\${fileName}`;
      console.log('Saving Locust file to VFS:', vfsPath, 'Content length:', content.length);
      await globalVFS.save(vfsPath, content);
      console.log('Saved successfully to VFS');
    }

    const locustConfig = `// Locust BattleMech Configuration
[General]
Name="LCT-1V Locust"
Weight=20
Class=Light
Manufacturer="Bergan Industries"
Model="LCT-1V"
Year=3025

[Performance]
MaxSpeed=129.6
JumpCapacity=0
HeatSinks=10
ArmorType=Standard
Structure=Standard

[Weapons]
Weapon1="Medium Laser"
Weapon1Location=CenterTorso
Weapon2="Machine Gun"
Weapon2Location=RightArm
Weapon2Ammo=100

[Armor]
Head=15
CenterTorso=25
RightArm=15
LeftArm=15
RightLeg=20
LeftLeg=20

[Description]
"The Locust is the fastest BattleMech in existence, capable of speeds that would make a hover vehicle jealous. 
What it lacks in armor and firepower, it makes up for in speed and maneuverability."`;

    await globalVFS.save('C:\\Games\\MekWarrior\\Mechs\\Locust\\Locust.mek', locustConfig);

    // Install Jenner Light Mech
    console.log('Installing Jenner Light Mech...');
    const jennerObjFiles = {
      'body.obj': jennerBodyOBJ,
      'leftarm.obj': jennerLeftArmOBJ,
      'rightarm.obj': jennerRightArmOBJ,
      'leftleg.obj': jennerLeftLegOBJ,
      'rightleg.obj': jennerRightLegOBJ
    };

    for (const [fileName, content] of Object.entries(jennerObjFiles)) {
      const vfsPath = `C:\\Games\\MekWarrior\\Mechs\\Jenner\\${fileName}`;
      await globalVFS.save(vfsPath, content);
    }

    const jennerConfig = `// Jenner BattleMech Configuration
[General]
Name="JR7-D Jenner"
Weight=35
Class=Light
Manufacturer="Diplan 'MechYards"
Model="JR7-D"
Year=3049

[Performance]
MaxSpeed=118.8
JumpCapacity=150
HeatSinks=10
ArmorType=Standard
Structure=Standard

[Weapons]
Weapon1="SRM-4"
Weapon1Location=LeftTorso
Weapon1Ammo=25
Weapon2="SRM-4"
Weapon2Location=RightTorso
Weapon2Ammo=25
Weapon3="Medium Laser"
Weapon3Location=CenterTorso
Weapon4="Medium Laser"
Weapon4Location=Head

[Armor]
Head=25
CenterTorso=45
LeftTorso=35
RightTorso=35
LeftArm=25
RightArm=25
LeftLeg=40
RightLeg=40

[Description]
"The Jenner is a well-balanced light BattleMech that combines speed with respectable firepower. 
Its jump jets allow for tactical positioning that heavier mechs cannot match."`;

    await globalVFS.save('C:\\Games\\MekWarrior\\Mechs\\Jenner\\Jenner.mek', jennerConfig);

    // Install Raven Light Mech
    console.log('Installing Raven Light Mech...');
    const ravenObjFiles = {
      'body.obj': ravenBodyOBJ,
      'leftarm.obj': ravenLeftArmOBJ,
      'rightarm.obj': ravenRightArmOBJ,
      'leftleg.obj': ravenLeftLegOBJ,
      'rightleg.obj': ravenRightLegOBJ
    };

    for (const [fileName, content] of Object.entries(ravenObjFiles)) {
      const vfsPath = `C:\\Games\\MekWarrior\\Mechs\\Raven\\${fileName}`;
      await globalVFS.save(vfsPath, content);
    }

    const ravenConfig = `// Raven BattleMech Configuration
[General]
Name="RVN-3L Raven"
Weight=35
Class=Light
Manufacturer="Technicron Manufacturing"
Model="RVN-3L"
Year=3058

[Performance]
MaxSpeed=97.2
JumpCapacity=180
HeatSinks=10
ArmorType=Ferro-Fibrous
Structure=Endo Steel

[Weapons]
Weapon1="Medium Laser"
Weapon1Location=LeftArm
Weapon2="SRM-6"
Weapon2Location=LeftTorso
Weapon2Ammo=15
Weapon3="TAG"
Weapon3Location=CenterTorso

[Equipment]
ECM=true
NARC=true

[Armor]
Head=25
CenterTorso=50
LeftTorso=35
RightTorso=35
LeftArm=25
RightArm=25
LeftLeg=40
RightLeg=40

[Description]
"The Raven is an advanced reconnaissance BattleMech equipped with sophisticated electronic warfare systems. 
Its ECM suite makes it nearly invisible to enemy sensors, perfect for scouting and hit-and-run tactics."`;

    await globalVFS.save('C:\\Games\\MekWarrior\\Mechs\\Raven\\Raven.mek', ravenConfig);

    console.log('Light mechs installation complete!');
  }

  /**
   * Check if MekWarrior is already installed
   */
  async isInstalled(): Promise<boolean> {
    try {
      const configExists = await globalVFS.exists('C:\\Games\\MekWarrior\\Config\\game.cfg');
      const mechExists = await globalVFS.exists('C:\\Games\\MekWarrior\\Mechs\\Annihilator\\Annihilator.mek');
      return configExists && mechExists;
    } catch {
      return false;
    }
  }

  /**
   * Uninstall MekWarrior (remove all game files)
   */
  async uninstall(): Promise<void> {
    console.log('Uninstalling MekWarrior...');
    try {
      // Note: VFS doesn't have recursive directory deletion yet
      // For now, we'll just clear the main files
      const filesToRemove = [
        'C:\\Games\\MekWarrior\\Config\\game.cfg',
        'C:\\Games\\MekWarrior\\Config\\controls.cfg',
        'C:\\Games\\MekWarrior\\README.TXT',
        'C:\\Games\\MekWarrior\\Mechs\\Annihilator\\Annihilator.mek',
        'C:\\Games\\MekWarrior\\Missions\\Annihilator_Briefing.txt'
      ];

      for (const file of filesToRemove) {
        try {
          await globalVFS.delete(file);
        } catch {
          // Ignore missing files
        }
      }

      console.log('MekWarrior uninstalled');
    } catch (error) {
      console.error('Failed to uninstall MekWarrior:', error);
      throw error;
    }
  }
}

export const gameInstaller = new GameInstaller();